import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  SCROLL_THRESHOLD_DISABLE, 
  SCROLL_THRESHOLD_ENABLE,
  NEAR_BOTTOM_THRESHOLD,
  SCROLL_ANIMATION_DELAY,
  SCROLL_RESET_DELAY,
  USER_SCROLL_END_DELAY
} from '../constants';
import { contentToString } from '../utils/contentHelpers';

/**
 * Custom hook for managing auto-scroll behavior in the chat area
 * @param {React.RefObject} chatRef - Reference to the scrollable chat container
 * @param {Array} validMessages - Array of valid messages
 * @param {string} agentStatus - Current agent status ('generating', 'stopped', etc.)
 * @returns {Object} Auto-scroll state and control functions
 */
export const useAutoScroll = (chatRef, validMessages, agentStatus) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  
  // Refs for scroll handling
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const isAutoScrollingRef = useRef(false);
  
  // Refs to track streaming message updates for auto-scroll
  const lastMessageContentLengthRef = useRef(0);
  const lastScrollHeightRef = useRef(0);

  /**
   * Scroll to the bottom of the chat container
   * @param {boolean} force - Force scroll even if user is scrolling
   */
  const scrollToBottom = useCallback((force = false) => {
    if (chatRef && chatRef.current && (force || !isUserScrollingRef.current)) {
      isAutoScrollingRef.current = true;
      isUserScrollingRef.current = false; // Clear user scrolling flag when forcing
      
      requestAnimationFrame(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
          // Reset auto-scrolling flag after scroll completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
            setIsNearBottom(true);
          }, SCROLL_ANIMATION_DELAY);
        }
      });
    }
    setTimeout(() => setAutoScroll(true), 0);
  }, [chatRef]);

  /**
   * Handle the start of a scroll event
   */
  const handleScrollStart = useCallback(() => {
    // Only mark as user scrolling if it's not an auto-scroll
    if (!isAutoScrollingRef.current) {
      isUserScrollingRef.current = true;
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  /**
   * Handle the end of a scroll event
   */
  const handleScrollEnd = useCallback(() => {
    scrollTimeoutRef.current = setTimeout(() => {
      // Only reset user scrolling if we're not auto-scrolling
      if (!isAutoScrollingRef.current) {
        isUserScrollingRef.current = false;
      }
    }, USER_SCROLL_END_DELAY);
  }, []);

  /**
   * Handle scroll events and update auto-scroll state
   */
  const handleScroll = useCallback(() => {
    if (chatRef && chatRef.current) {
      requestAnimationFrame(() => {
        if (!chatRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        const nearBottom = distanceFromBottom < NEAR_BOTTOM_THRESHOLD;
        
        // Don't update isNearBottom state during auto-scroll to prevent button flashing
        if (!isAutoScrollingRef.current) {
          setIsNearBottom(nearBottom);
        }
        
        // Use different thresholds to prevent rapid toggling (hysteresis)
        // Only disable auto-scroll when clearly scrolling away from bottom
        if (!nearBottom && autoScroll && distanceFromBottom > SCROLL_THRESHOLD_DISABLE && !isAutoScrollingRef.current) {
          setAutoScroll(false);
        }
        
        // Only re-enable auto-scroll when very close to bottom
        if (nearBottom && !autoScroll && distanceFromBottom < SCROLL_THRESHOLD_ENABLE) {
          setAutoScroll(true);
        }
      });
    }
  }, [chatRef, autoScroll]);

  /**
   * Enhanced scroll handler that detects user intent
   */
  const enhancedHandleScroll = useCallback((e) => {
    // Don't interfere with auto-scrolling
    if (isAutoScrollingRef.current) {
      return;
    }
    
    handleScrollStart();
    
    // If user is scrolling up while near bottom, immediately disable auto-scroll
    if (chatRef && chatRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Check if user initiated scroll (comparing with last known position)
      if (distanceFromBottom > 10 && isNearBottom && autoScroll && !isAutoScrollingRef.current) {
        setAutoScroll(false);
      }
    }
    
    handleScroll();
    handleScrollEnd();
  }, [handleScroll, handleScrollStart, handleScrollEnd, isNearBottom, autoScroll, chatRef]);

  /**
   * Handle wheel scroll events
   */
  const handleWheelScroll = useCallback(e => {
    // Only disable auto-scroll for intentional user scrolling up, not during auto-scroll
    if (e.deltaY < 0 && !isAutoScrollingRef.current) {
      setAutoScroll(false);
      handleScrollStart(); // Mark as user scrolling
    }
  }, [handleScrollStart]);

  // Enhanced auto-scroll effect that handles streaming message updates
  useEffect(() => {
    if (!chatRef?.current) return;

    const chatElement = chatRef.current;
    const currentScrollHeight = chatElement.scrollHeight;

    // Check if this is a streaming message update by comparing scroll height
    const isContentGrowing = currentScrollHeight > lastScrollHeightRef.current;
    lastScrollHeightRef.current = currentScrollHeight;

    // Check if the last message is streaming
    const lastMessage = validMessages[validMessages.length - 1];
    const isLastMessageStreaming = lastMessage && (
      agentStatus === 'generating' && (
        lastMessage.isStreaming === true ||
        lastMessage.is_streaming === true ||
        (typeof lastMessage.isStreaming === 'undefined' &&
         typeof lastMessage.is_streaming !== 'undefined' &&
         lastMessage.is_streaming === true)
      )
    );

    // Auto-scroll if:
    // 1. User is not actively scrolling AND
    // 2. (Content is growing OR last message is streaming) AND
    // 3. Auto-scroll is enabled
    if (autoScroll && !isUserScrollingRef.current && (isContentGrowing || isLastMessageStreaming)) {
      isAutoScrollingRef.current = true;

      const performScroll = () => {
        const canScroll = chatElement && autoScroll && !isUserScrollingRef.current;
        if (canScroll) {
          chatElement.scrollTop = chatElement.scrollHeight;
          setIsNearBottom(true);

          if (isLastMessageStreaming) {
            requestAnimationFrame(() => {
              if (chatElement && autoScroll && !isUserScrollingRef.current) {
                chatElement.scrollTop = chatElement.scrollHeight;
                setIsNearBottom(true);
              }
            });
          }
        }

        // Reset auto-scrolling flag after scroll completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, SCROLL_RESET_DELAY);
      };

      // Schedule for next frame to ensure DOM is painted
      requestAnimationFrame(performScroll);
    }
  }, [validMessages, autoScroll, chatRef, agentStatus]);

  // Additional effect to handle streaming message content changes
  useEffect(() => {
    if (!chatRef?.current || !autoScroll || validMessages.length === 0) return;

    const lastMessage = validMessages[validMessages.length - 1];
    if (!lastMessage) return;

    const currentContentLength = contentToString(lastMessage.content).length;
    const isContentGrowing = currentContentLength > lastMessageContentLengthRef.current;
    lastMessageContentLengthRef.current = currentContentLength;

    // If the last message content is growing and we're near the bottom, scroll immediately
    if (isContentGrowing && isNearBottom && !isUserScrollingRef.current) {
      isAutoScrollingRef.current = true;
      
      // Use immediate scroll for content updates
      const performContentScroll = () => {
        if (chatRef.current && autoScroll && !isUserScrollingRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
          
          // Schedule another scroll check for rapid content updates
          requestAnimationFrame(() => {
            if (chatRef.current && autoScroll && !isUserScrollingRef.current) {
              chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
          });
        }
        
        // Reset auto-scrolling flag after scroll completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, SCROLL_RESET_DELAY);
      };
      
      requestAnimationFrame(performContentScroll);
    }
  }, [validMessages, autoScroll, chatRef, isNearBottom]);

  // Expose scroll control methods on the chat ref
  useEffect(() => {
    if (chatRef && typeof chatRef === 'object' && chatRef.current) {
      chatRef.current.scrollToBottom = () => {
        scrollToBottom(true); // Use the main scrollToBottom function with force flag
      };
      chatRef.current.isAutoScrollEnabled = autoScroll;
    }
  }, [autoScroll, chatRef, scrollToBottom]);

  // Cleanup effect for scroll timeout
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    autoScroll,
    isNearBottom,
    scrollToBottom,
    enhancedHandleScroll,
    handleWheelScroll,
    handleScrollStart
  };
};